import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

const MAX_POLLS = 8;

export default function InvestSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | success | pending | error
  const [amount, setAmount] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState('error'); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setAmount(data.amount);
        setState('success');
        return;
      }
      if (data.status === 'expired' || attempt >= MAX_POLLS) { setState('pending'); return; }
      setTimeout(() => poll(attempt + 1), 1500);
    } catch {
      if (attempt >= MAX_POLLS) setState('error');
      else setTimeout(() => poll(attempt + 1), 1500);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto py-10 px-5" style={{ background: '#0A0D0B' }} data-testid="invest-success-page">
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10 text-center"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="text-white text-3xl font-medium tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Apstiprinām maksājumu...</h1>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-500/15 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-green-400" />
            </div>
            <h1 className="text-white text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Paldies, investor! <Sparkles className="inline w-6 h-6 text-amber-400" />
            </h1>
            <p className="text-white/60 text-sm mb-7">
              {amount ? `Tavs ieguldījums €${Number(amount).toFixed(2)} ir saņemts. ` : ''}
              Tu tagad esi KoLV investors un piedalies 10% peļņas sadalē.
            </p>
            <Link to="/">
              <button className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors" style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}>
                Uz sākumu <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="text-white text-2xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Maksājums tiek apstrādāts</h1>
            <p className="text-white/60 text-sm mb-6">Tavs investora statuss tiks aktivizēts, tiklīdz maksājums būs apstiprināts.</p>
            <Link to="/"><button className="w-full h-12 rounded-full bg-white/10 text-white font-semibold text-sm hover:bg-white/15 transition-colors">Uz sākumu</button></Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-500/15 flex items-center justify-center">
              <XCircle className="w-9 h-9 text-red-400" />
            </div>
            <h1 className="text-white text-2xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Neizdevās pārbaudīt</h1>
            <p className="text-white/60 text-sm mb-6">Ja maksājums tika veikts, statuss atjaunosies drīzumā.</p>
            <Link to="/"><button className="w-full h-12 rounded-full bg-white/10 text-white font-semibold text-sm hover:bg-white/15 transition-colors">Uz sākumu</button></Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
